
import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { narrativeProjects, commercialProjects, documentaryProjects, reelProjects } from '@/mocks/projects';

export default function VideoPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const videoId = searchParams.get('id');
  const title = searchParams.get('title') || 'Video';
  const from = searchParams.get('from') || 'narrative';
  
  // Extract Vimeo video ID from URL
  const getVimeoId = (url: string) => {
    const match = url.match(/vimeo\.com\/(\d+)/);
    return match ? match[1] : null;
  };

  // Extract YouTube video ID from URL
  const getYouTubeId = (url: string) => {
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
    return match ? match[1] : null;
  };

  const vimeoId = videoId ? getVimeoId(decodeURIComponent(videoId)) : null;
  const youtubeId = videoId ? getYouTubeId(decodeURIComponent(videoId)) : null;

  const handleBackToGallery = () => {
    navigate(`/${from}`);
  };

  // Get project info based on title
  const getProjectInfo = () => {
    const urlTitle = searchParams.get('title') || '';
    const allProjects = [...narrativeProjects, ...commercialProjects, ...documentaryProjects, ...reelProjects];
    
    // Helper function to normalize strings for comparison
    const normalize = (str: string) => {
      return str
        .toLowerCase()
        .normalize('NFD') // Decompose accented characters
        .replace(/[\u0300-\u036f]/g, '') // Remove accent marks
        .replace(/[^a-z0-9]/g, ''); // Remove all non-alphanumeric characters
    };
    
    const normalizedUrlTitle = normalize(urlTitle);
    
    // Try multiple matching strategies
    for (const project of allProjects) {
      const projectTitle = project.title || '';
      const normalizedProjectTitle = normalize(projectTitle);
      
      // Strategy 1: Exact match
      if (projectTitle === urlTitle) {
        return project;
      }
      
      // Strategy 2: Normalized match (handles accents, spaces, special chars)
      if (normalizedProjectTitle === normalizedUrlTitle) {
        return project;
      }
      
      // Strategy 3: Check if project link contains the title
      if (project.link && project.link.includes(encodeURIComponent(urlTitle))) {
        return project;
      }
      
      // Strategy 4: Partial match (one contains the other)
      if (normalizedProjectTitle.includes(normalizedUrlTitle) || 
          normalizedUrlTitle.includes(normalizedProjectTitle)) {
        return project;
      }
    }
    
    return null;
  };

  const projectInfo = getProjectInfo();
  const projectDescription = projectInfo?.description;

  return (
    <div className="min-h-screen bg-black">
      <Header />
      
      <main className="pt-20">
        {/* Video Player Section */}
        <section className="py-8">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Video Title */}
            <div className="text-center mb-8">
              <h1 className="text-2xl md:text-3xl font-light text-white tracking-wider uppercase">
                {decodeURIComponent(title)}
              </h1>
            </div>

            {/* Video Player */}
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              {vimeoId ? (
                <iframe
                  src={`https://player.vimeo.com/video/${vimeoId}?autoplay=1&title=0&byline=0&portrait=0`}
                  className="absolute top-0 left-0 w-full h-full"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  title={decodeURIComponent(title)}
                />
              ) : youtubeId ? (
                <iframe
                  src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1`}
                  className="absolute top-0 left-0 w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={decodeURIComponent(title)}
                />
              ) : (
                <div className="absolute top-0 left-0 w-full h-full bg-gray-900 flex items-center justify-center">
                  <p className="text-white text-lg">Video not available</p>
                </div>
              )}
            </div>

            {/* Video Info */}
            {projectDescription && (
              <div className="mt-8 text-center">
                <div className="max-w-2xl mx-auto">
                  <p className="text-gray-300 text-base leading-relaxed whitespace-pre-line">
                    {projectDescription}
                  </p>
                </div>
              </div>
            )}

            {/* Back Button */}
            <div className="mt-12 text-center">
              <button
                onClick={handleBackToGallery}
                className="inline-flex items-center px-6 py-3 bg-white text-black font-medium tracking-wider uppercase transition-colors duration-300 hover:bg-gray-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line mr-2"></i>
                Back to Gallery
              </button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
